/* Copyright (c) 2020-present CloudIO Inc */

import { Avatar, Box, Divider, Typography } from '@mui/material';
import { getAvatarChars } from 'common/functions';
import { useCurrentRecord } from 'components/DataStoreReference/DataStoreReference';
import { useUserProfile } from 'components/UserPic/UserProfileCache';
import Icon from 'icons/Icon';
import tinyColor from 'tinycolor2';
import { getColor } from 'init/initFunctions';
import React from 'react';
import ReactTimeago from 'react-timeago';

interface AnyRecord {
  createdBy: string;
  lastUpdatedBy: string;
  creationDate: string;
  lastUpdateDate: string;
}

function Line({
  iconName,
  text,
  on = true,
}: {
  iconName: string;
  text: React.ReactNode;
  on?: boolean;
}) {
  if (!on) return null;
  return (
    <Typography
      sx={{ display: 'flex', alignItems: 'flex-start', fontSize: '0.8em' }}
    >
      <Icon
        iconName={iconName}
        width={12}
        height={12}
        style={{ marginTop: '4px' }}
      />
      <Box component="span" sx={{ flex: 1, paddingLeft: 2, paddingRight: 1 }}>
        {text}
      </Box>
    </Typography>
  );
}

function Who({
  userName,
  update = false,
}: {
  userName: string;
  update?: boolean;
}) {
  const record = useCurrentRecord<AnyRecord>();
  const updated =
    (update ? record?.lastUpdatedBy : record?.createdBy) === userName;
  const date = update ? record?.lastUpdateDate : record?.creationDate;
  if (!(updated && date)) {
    return null;
  }
  return (
    <Line
      iconName={update ? 'edit' : 'plus'}
      text={
        <>
          {update ? 'Updated' : 'Created'} this record{' '}
          <ReactTimeago date={date} />
        </>
      }
    />
  );
}

export default function UserProfilePopover({
  userName,
  onClose,
  children,
}: {
  userName: string;
  onClose: () => void;
  children?: React.ReactNode;
}) {
  const profile = useUserProfile(userName);
  if (!profile) {
    return null;
  }
  const bgcolor = getColor(profile.emailAddress);
  return (
    <Box
      sx={{
        display: 'flex',
        borderRadius: 1,
        flexDirection: 'column',
        color: 'text.primary',
        maxWidth: 400,
        minWidth: 320,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          padding: 1,
        }}
      >
        <Avatar
          src={profile.profileImage ?? ''}
          sx={{
            borderRadius: '50%',
            marginLeft: 1,
            width: 64,
            height: 64,
            bgcolor,
            color: tinyColor
              .mostReadable(bgcolor, ['#fff', '#000'])
              .toHexString(),
          }}
        >
          {getAvatarChars(profile.displayName)}
        </Avatar>
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            marginLeft: 2,
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyItems: 'flex-start',
            }}
          >
            {profile.displayName ? (
              <Box
                sx={{
                  fontWeight: 500,
                }}
              >
                {profile.displayName}
              </Box>
            ) : null}
            <Box
              sx={{
                fontSize: '0.8em',
                marginLeft: profile.displayName ? 2 : 0,
                fontWeight: 300,
                textDecoration: profile.endDate ? 'line-through' : 'none',
              }}
            >
              {userName}
            </Box>
          </Box>

          {profile.emailAddress ? (
            <Box
              sx={{
                fontSize: '0.8em',
                fontWeight: 300,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                textDecoration: profile.endDate ? 'line-through' : 'none',
              }}
            >
              <Icon width={12} height={12} iconName="envelope" />{' '}
              {profile.emailAddress}
            </Box>
          ) : null}
          <Box
            sx={{
              fontSize: '0.8em',
              fontWeight: 300,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Icon width={12} height={12} iconName="map-marker-alt" />{' '}
            {profile.location ?? 'Unknown'}
          </Box>
        </Box>
      </Box>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: 2,
          gap: '4px',
          justifyItems: 'center',
        }}
      >
        <Who userName={userName} />
        <Who userName={userName} update />
        <Line
          iconName="calendar-plus"
          on={!!profile.startDate}
          text={
            <>
              Joined <ReactTimeago date={profile.startDate} />
            </>
          }
        />
        {profile.endDate ? (
          <Line
            iconName="calendar-times"
            text={
              <>
                Deactivated <ReactTimeago date={profile.startDate} />
              </>
            }
          />
        ) : null}
        <Line
          iconName="users"
          text={`Member of ${profile.roles} roles`}
          on={!!profile.roles}
        />
        <Line
          iconName="building"
          text={`Member of ${profile.apps} apps`}
          on={!!profile.apps}
        />
      </Box>
      {children}
    </Box>
  );
}

export function UserDisplayName({ userName }: { userName: string }) {
  const user = useUserProfile(userName);
  return <Typography>{user?.displayName || userName}</Typography>;
}
